import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { User } from '../api/auth'
import { BalanceCoin } from '../icons/BalanceCoin.tsx'
import { creditsState, hasPaidPeriod } from '../utils/creditsState'
import { useNotifications } from '../context/NotificationContext'
import { useAuthLink } from '../hooks/useAuthModal'
import NotificationPanel from './NotificationPanel'
import TopUpSlider from '../pages/account/components/TopUpSlider'

type Props = {
  user: User | null
  theme: 'light' | 'dark'
  onAccountClick: () => void
  showLoginBtn?: boolean
}

export default function HeaderActions({ user, theme, onAccountClick, showLoginBtn = true }: Props) {
  const { t } = useTranslation()
  const location = useLocation()
  // «Войти» гостя — окном поверх страницы (useAuthLink): страница остаётся под окном
  const authLink = useAuthLink()
  const balance = user ? (user.credits_balance ?? 0) : 0
  const credits = balance.toFixed(0)
  // Состояние баланса по порогам суммы: цвет искры и вид пилюли меняются ДО
  // того, как юзер упрётся в отказ (решение владельца: «чтобы бросалось в
  // глаза»). Зависит ТОЛЬКО от баланса — значит одинаково на всех страницах.
  const state = creditsState(balance)
  const { unreadCount, markAllRead } = useNotifications()
  const [panelOpen, setPanelOpen] = useState(false)
  const [topupOpen, setTopupOpen] = useState(false)
  // Момент монтирования — для сравнения срока пакета (Date.now() в рендере
  // запрещён react-compiler'ом; погрешность «истёк, пока вкладка висела»
  // несущественна)
  const [mountedAt] = useState(() => Date.now())

  // Оплаченный период: подписка active/trial ИЛИ разовый пакет в сроке
  // (subscription_expires_at живёт и у разовых — конец пакета). Такому юзеру
  // клик по балансу открывает ДОКУПКУ токенов, а не витрину тарифов: у
  // купившего вопрос «где ещё токенов», не «какой тариф выбрать» (решение
  // владельца 2026-08-08). Free/истёкший период — прежний путь на /tariffs.
  const paidPeriod = hasPaidPeriod(user, mountedAt)

  const handleBellClick = () => {
    if (!panelOpen) {
      setPanelOpen(true)
      markAllRead()
    } else {
      setPanelOpen(false)
    }
  }

  // Пилюля баланса — общие куски для обеих подач (ссылка/кнопка)
  const pillTitle =
    state.level === 'empty' ? 'Токены закончились — пополнить'
      : state.level === 'critical' ? `Осталось ${credits} токенов — пополнить`
      : state.level === 'low' ? `Токены заканчиваются: ${credits} — пополнить`
      : paidPeriod ? 'Докупить токены'
      : t('header.balance')
  /* --warming вешаем только когда заливка есть: у здорового баланса
     в шапке не должна крутиться бесконечная анимация волны.
     --alarming (ниже 100) гасит жёлтую волну и оставляет красную —
     внимание на ней. */
  const pillClass =
    `header-credits header-credits--${state.level}`
    + (state.warm > 0 ? ' header-credits--warming' : '')
    + (state.alarm > 0 ? ' header-credits--alarming' : '')
  /* Доли заливки пилюли (0…1): жёлтая набирается 200→100, красная
     идёт поверх неё 100→0. Рисуются в _layout.scss. */
  const pillStyle = {
    '--credits-warm': state.warm,
    '--credits-alarm': state.alarm,
  } as React.CSSProperties
  const pillInner = (
    <>
      {/* Нулевой баланс — пилюля становится действием: цифра «0» ничего
          не подсказывает, а «Пополнить» ведёт к решению. */}
      {state.level === 'empty' ? (
        <span className="credits-value">Пополнить</span>
      ) : (
        <span className="credits-value">{credits}</span>
      )}
      <BalanceCoin size={22} />
    </>
  )

  return (
    <div className="header-actions">
      {user ? (
        <>
          {paidPeriod ? (
            <button
              type="button"
              className={`${pillClass} header-credits--btn`}
              style={pillStyle}
              title={pillTitle}
              onClick={() => setTopupOpen(true)}
            >
              {pillInner}
            </button>
          ) : (
            <Link
              to="/tariffs"
              state={{ backgroundLocation: location }}
              title={pillTitle}
              className={pillClass}
              style={pillStyle}
            >
              {pillInner}
            </Link>
          )}
          <TopUpSlider open={topupOpen} onClose={() => setTopupOpen(false)} />

          <div className="header-notification-wrapper">
            <button className="header-icon-btn" title={t('header.notifications')} onClick={handleBellClick}>
              <img
                src={theme === 'dark' ? '/icons/noty_dark.svg' : '/icons/noty_light.svg'}
                alt=""
                width="20"
                height="20"
              />
              {unreadCount > 0 && <span className="notification-dot"></span>}
            </button>
            <NotificationPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
          </div>
        </>
      ) : showLoginBtn ? (
        // Unauthenticated: единая кнопка «Войти» с иконкой + текстом.
        // Раньше отрисовывалось ДВЕ кнопки (login-btn + account-btn) обе на
        // /login — избыточно. Account-btn теперь только для authenticated.
        <a {...authLink('/login')} className="header-login-btn" title={t('header.login')}>
          <img src="/icons/user_dark.svg" alt="" width="16" height="16" />
          <span className="header-login-btn__label">{t('header.login')}</span>
        </a>
      ) : null}
      {user && (
        <a
            href="/account"
            className="header-icon-btn header-account-btn"
            title={t('header.account')}
            onClick={(e) => {
              if (
                  e.metaKey ||
                  e.ctrlKey ||
                  e.shiftKey ||
                  e.button !== 0
              ) {
                return;
              }
              e.preventDefault();
              onAccountClick();
            }}
        >
          {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="header-avatar" />
          ) : (
              <img
                  src={theme === "dark" ? "/icons/user_dark.svg" : "/icons/user_light.svg"}
                  alt=""
                  width="20"
                  height="20"
              />
          )}
        </a>
      )}
    </div>
  )
}
